import AdminMessagesContainer from './AdminMessagesContainer';
import { Message } from 'types/Message';
import { FormattedMessage } from 'react-intl';
import { ReactNode, useCallback, useEffect, useState } from 'react';
import { Button, Grid, Paper } from '@mui/material';
import useStyles from './AdminMessageViewer.styles';
import AdminMessagesDeleteButton from './AdminMessagesDeleteButton/AdminMessagesDeleteButton';
import { AdminMessagesContextProvider } from './AdminMessagesContext';
import { navigateToSelectedChannelRoute } from 'utils/history';
import Route from 'routes/Routes';
import { useSelectedLinkContext } from 'contexts';
import AdminMessageViewerAttachments from './AdminMessageViewer/AdminMessageViewerAttachments';
import AdminMessageBodyLoader from './AdminMessageViewer/AdminMessageBodyLoader';
import useSmallDesktopScreen from 'hooks/useSmallDesktopScreen';
import { useQuery } from '@apollo/client';
import {
  GET_MESSAGE_QUERY,
  GetMessageOutputType,
} from 'graphql/admin/adminmessage/AdminMessageQueries';
import { AdminMessagesParams } from 'types/RouteParams';
import { useParams } from 'react-router';
import {Paperclip} from 'components/icons';
import {DateAndHour} from 'components';

type AdminMessageViewerPureProps = {
  bodyUrl: string | undefined;
  date: ReactNode;
  destEmails: string;
  isSmallScreen: boolean;
  message: Message;
  handleGoBackClick: () => void;
};

const AdminMessageViewerPure = ({
  bodyUrl,
  date,
  destEmails,
  isSmallScreen,
  message,
  handleGoBackClick,
}: AdminMessageViewerPureProps): JSX.Element => {
  const classes = useStyles();

  return (
    <AdminMessagesContainer>
      <Grid container>
        <Grid item xs={6}>
          <Button className={classes.goBackButton} onClick={handleGoBackClick}>
            <FormattedMessage id="button.back" />
          </Button>
        </Grid>

        <Grid className={classes.deleteButtonContainer} item xs={6}>
          <AdminMessagesDeleteButton isUniqueDeletion />
        </Grid>

        <Grid className={classes.messageContainer} item xs={12}>
          <Paper className={classes.paper}>
            <div className={classes.attachmentIconContainer}>
              {message.hasAttachment && <Paperclip fontSize={isSmallScreen ? 'small' : 'medium'} />}
            </div>

            <Grid container>
              <Grid className={classes.subject} item md={10} xs={12}>
                <FormattedMessage id="admin.messages.messageView.subject" /> :{' '}
                {message.subject}
              </Grid>
              <Grid className={classes.date} item md={2} xs={12}>
                {date}
              </Grid>

              <Grid className={classes.from} item xs={12}>
                <FormattedMessage id="admin.messages.messageView.from" /> :{' '}
                {message.senderAddress}
              </Grid>

              <Grid className={classes.to} item xs={12}>
                <FormattedMessage id="admin.messages.messageView.to" /> :{' '}
                {destEmails}
              </Grid>

              <Grid className={classes.body} item xs={12}>
                <AdminMessageBodyLoader s3URL={bodyUrl} />
              </Grid>
            </Grid>

            <AdminMessageViewerAttachments attachments={message.attachments} />
          </Paper>
        </Grid>
      </Grid>
    </AdminMessagesContainer>
  );
};

const AdminMessageViewer = (): JSX.Element => {
  const { key } = useParams<AdminMessagesParams>();
  const { selectedLink } = useSelectedLinkContext();
  const isSmallScreen = useSmallDesktopScreen();

  const [selectedMessages, setSelectedMessages] = useState<Message[]>([]);

  const { data, loading } = useQuery<GetMessageOutputType>(GET_MESSAGE_QUERY, {
    variables: {
      getMessageInput: {
        channel: selectedLink?.channelKey,
        messageKey: key,
      },
    },
  });

  const message = data?.getMessage?.message;

  useEffect(() => {
    if (message) {
      setSelectedMessages([message]);
    }
  }, [message]);

  const handleGoBackClick = useCallback(() => navigateToSelectedChannelRoute(selectedLink, Route.ADMIN_MESSAGES), [selectedLink]);

  if (!message || loading) {
    return null;
  }

  const date = <DateAndHour>{message.date}</DateAndHour>;
  const destEmails = message.destAddress.join(', ');

  return (
    <AdminMessagesContextProvider
      selectedMessages={selectedMessages}
      setSelectedMessages={setSelectedMessages}
    >
      <AdminMessageViewerPure
        bodyUrl={message.generateHTML}
        date={date}
        destEmails={destEmails}
        isSmallScreen={isSmallScreen}
        message={message}
        handleGoBackClick={handleGoBackClick}
      />
    </AdminMessagesContextProvider>
  );
};

export default AdminMessageViewer;
